const ACTIVE_CART_KEY_PREFIX = 'active_cart_by_store:';
const ACTIVE_CART_SNAPSHOT_KEY_PREFIX = 'active_cart_snapshot_by_store:';
const ACTIVE_SHARED_CART_EXPLICIT_BY_STORE_KEY_PREFIX = 'active_shared_cart_explicit_by_store:';
const ACTIVE_SHARED_CART_THEME_PRIORITY_KEY = 'active_shared_cart_theme_priority';
const ACTIVE_SHARED_CART_THEME_PRIORITY_EVENT = 'active-shared-cart-theme-priority-changed';
const ACTIVE_SHARED_CART_STATE_CHANGED_EVENT = 'active-shared-cart-state-changed';
const ACTIVE_COLLABORATIVE_ACTIVITY_SIGNAL_EVENT = 'active-collaborative-activity-signal';
const ACTIVE_SHARED_CART_ROLE_KEY = 'active_shared_cart_role';
const ACTIVE_SHARED_CART_GUEST_OWNER_COLOR_KEY = 'active_shared_cart_guest_owner_color';
const ACTIVE_SHARED_CART_COLLABORATOR_COLOR_KEY = 'active_shared_cart_collaborator_color';
const ACTIVE_CART_DEBUG = true;

const debugActiveCart = (...args: any[]) => {
  if (!ACTIVE_CART_DEBUG) return;
  console.log('[ActiveCartPreference]', ...args);
};

const buildKey = (storeId: string) => `${ACTIVE_CART_KEY_PREFIX}${storeId}`;
const buildSnapshotKey = (storeId: string) => `${ACTIVE_CART_SNAPSHOT_KEY_PREFIX}${storeId}`;
const buildSharedExplicitKey = (storeId: string) => `${ACTIVE_SHARED_CART_EXPLICIT_BY_STORE_KEY_PREFIX}${storeId}`;

const emitActiveSharedCartStateChanged = () => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(ACTIVE_SHARED_CART_STATE_CHANGED_EVENT, {
      detail: {
        role: getActiveSharedCartRole(),
        guestOwnerColor: getActiveSharedCartGuestOwnerColor(),
        collaboratorColor: getActiveSharedCartCollaboratorColor(),
        themePriority: getActiveSharedCartThemePriority(),
      },
    }));
  } catch {}
};

export type ActiveCartSnapshot = {
  storeId: string;
  cartId: string;
  isShared: boolean;
  sharedToken?: string | null;
  units: number;
  total: number;
  storeName?: string;
  storeSlug?: string;
  updatedAt: string;
};

export const getActiveCartPreference = (storeId: string): string | null => {
  if (!storeId || typeof window === 'undefined') {
    debugActiveCart('get:skipped', { storeId, hasWindow: typeof window !== 'undefined' });
    return null;
  }
  try {
    const value = localStorage.getItem(buildKey(storeId));
    debugActiveCart('get:ok', { storeId, value });
    return value;
  } catch (error) {
    debugActiveCart('get:error', { storeId, error });
    return null;
  }
};

export const setActiveCartPreference = (storeId: string, cartId: string): void => {
  if (!storeId || !cartId || typeof window === 'undefined') {
    debugActiveCart('set:skipped', { storeId, cartId, hasWindow: typeof window !== 'undefined' });
    return;
  }
  try {
    localStorage.setItem(buildKey(storeId), cartId);
    debugActiveCart('set:ok', { storeId, cartId });
  } catch (error) {
    debugActiveCart('set:error', { storeId, cartId, error });
    // Ignore storage write errors.
  }
};

export const clearActiveCartPreference = (storeId: string): void => {
  if (!storeId || typeof window === 'undefined') {
    debugActiveCart('clear:skipped', { storeId, hasWindow: typeof window !== 'undefined' });
    return;
  }
  try {
    localStorage.removeItem(buildKey(storeId));
    debugActiveCart('clear:ok', { storeId });
  } catch (error) {
    debugActiveCart('clear:error', { storeId, error });
    // Ignore storage removal errors.
  }
};

export const setActiveCartSnapshot = (snapshot: ActiveCartSnapshot): void => {
  if (!snapshot?.storeId || typeof window === 'undefined') {
    debugActiveCart('snapshot:set:skipped', { storeId: snapshot?.storeId, hasWindow: typeof window !== 'undefined' });
    return;
  }
  try {
    localStorage.setItem(buildSnapshotKey(snapshot.storeId), JSON.stringify(snapshot));
    debugActiveCart('snapshot:set:ok', {
      storeId: snapshot.storeId,
      cartId: snapshot.cartId,
      isShared: snapshot.isShared,
      sharedToken: snapshot.sharedToken || null,
      units: snapshot.units,
      total: snapshot.total,
    });
  } catch (error) {
    debugActiveCart('snapshot:set:error', { storeId: snapshot.storeId, error });
  }
};

export const getActiveCartSnapshot = (storeId: string): ActiveCartSnapshot | null => {
  if (!storeId || typeof window === 'undefined') {
    debugActiveCart('snapshot:get:skipped', { storeId, hasWindow: typeof window !== 'undefined' });
    return null;
  }
  try {
    const raw = localStorage.getItem(buildSnapshotKey(storeId));
    if (!raw) {
      debugActiveCart('snapshot:get:empty', { storeId });
      return null;
    }
    const parsed = JSON.parse(raw) as ActiveCartSnapshot;
    debugActiveCart('snapshot:get:ok', {
      storeId,
      cartId: parsed?.cartId,
      isShared: parsed?.isShared,
      sharedToken: parsed?.sharedToken || null,
      units: parsed?.units,
      total: parsed?.total,
    });
    return parsed;
  } catch (error) {
    debugActiveCart('snapshot:get:error', { storeId, error });
    return null;
  }
};

export const getActiveCartSnapshotByStoreSlug = (storeSlug: string): ActiveCartSnapshot | null => {
  if (!storeSlug || typeof window === 'undefined') {
    debugActiveCart('snapshot:getBySlug:skipped', { storeSlug, hasWindow: typeof window !== 'undefined' });
    return null;
  }

  try {
    const slug = String(storeSlug).toLowerCase();
    const snapshots: ActiveCartSnapshot[] = [];

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(ACTIVE_CART_SNAPSHOT_KEY_PREFIX)) continue;

      const raw = localStorage.getItem(key);
      if (!raw) continue;

      try {
        const parsed = JSON.parse(raw) as ActiveCartSnapshot;
        if (parsed?.storeSlug && String(parsed.storeSlug).toLowerCase() === slug) {
          snapshots.push(parsed);
        }
      } catch {
        // Ignore malformed snapshot payloads.
      }
    }

    if (snapshots.length === 0) {
      debugActiveCart('snapshot:getBySlug:empty', { storeSlug });
      return null;
    }

    snapshots.sort((a, b) => {
      const ta = new Date(a.updatedAt || 0).getTime();
      const tb = new Date(b.updatedAt || 0).getTime();
      return tb - ta;
    });

    const latest = snapshots[0];
    debugActiveCart('snapshot:getBySlug:ok', {
      storeSlug,
      storeId: latest.storeId,
      cartId: latest.cartId,
      isShared: latest.isShared,
      sharedToken: latest.sharedToken || null,
    });
    return latest;
  } catch (error) {
    debugActiveCart('snapshot:getBySlug:error', { storeSlug, error });
    return null;
  }
};

export const setActiveSharedCartRole = (role: 'owner' | 'guest' | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (role) {
      localStorage.setItem(ACTIVE_SHARED_CART_ROLE_KEY, role);
    } else {
      localStorage.removeItem(ACTIVE_SHARED_CART_ROLE_KEY);
    }
    if (role !== 'owner') {
      localStorage.removeItem(ACTIVE_SHARED_CART_COLLABORATOR_COLOR_KEY);
    }
    emitActiveSharedCartStateChanged();
  } catch {}
};

export const getActiveSharedCartRole = (): 'owner' | 'guest' | null => {
  if (typeof window === 'undefined') return null;
  try {
    const v = localStorage.getItem(ACTIVE_SHARED_CART_ROLE_KEY);
    if (v === 'owner' || v === 'guest') return v;
    return null;
  } catch {
    return null;
  }
};

export const setActiveSharedCartGuestOwnerColor = (hex: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (hex) {
      localStorage.setItem(ACTIVE_SHARED_CART_GUEST_OWNER_COLOR_KEY, hex);
    } else {
      localStorage.removeItem(ACTIVE_SHARED_CART_GUEST_OWNER_COLOR_KEY);
    }
    emitActiveSharedCartStateChanged();
  } catch {}
};

export const getActiveSharedCartGuestOwnerColor = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(ACTIVE_SHARED_CART_GUEST_OWNER_COLOR_KEY);
  } catch {
    return null;
  }
};

export const setActiveSharedCartCollaboratorColor = (hex: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (hex) {
      localStorage.setItem(ACTIVE_SHARED_CART_COLLABORATOR_COLOR_KEY, hex);
    } else {
      localStorage.removeItem(ACTIVE_SHARED_CART_COLLABORATOR_COLOR_KEY);
    }
    emitActiveSharedCartStateChanged();
  } catch {}
};

export const getActiveSharedCartCollaboratorColor = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(ACTIVE_SHARED_CART_COLLABORATOR_COLOR_KEY);
  } catch {
    return null;
  }
};

export const setActiveSharedCartThemePriority = (isEnabled: boolean): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(ACTIVE_SHARED_CART_THEME_PRIORITY_KEY, isEnabled ? '1' : '0');
    window.dispatchEvent(new CustomEvent(ACTIVE_SHARED_CART_THEME_PRIORITY_EVENT, {
      detail: { isEnabled },
    }));
    emitActiveSharedCartStateChanged();
    debugActiveCart('shared-theme-priority:set', { isEnabled });
  } catch (error) {
    debugActiveCart('shared-theme-priority:set:error', { isEnabled, error });
  }
};

export const getActiveSharedCartThemePriority = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(ACTIVE_SHARED_CART_THEME_PRIORITY_KEY) === '1';
  } catch (error) {
    debugActiveCart('shared-theme-priority:get:error', { error });
    return false;
  }
};

export const ACTIVE_SHARED_CART_THEME_PRIORITY_CHANGED_EVENT = ACTIVE_SHARED_CART_THEME_PRIORITY_EVENT;
export const ACTIVE_SHARED_CART_STATE_CHANGED = ACTIVE_SHARED_CART_STATE_CHANGED_EVENT;

export const setActiveSharedCartExplicitByStore = (storeId: string, isEnabled: boolean): void => {
  if (!storeId || typeof window === 'undefined') return;
  try {
    if (isEnabled) {
      localStorage.setItem(buildSharedExplicitKey(storeId), '1');
    } else {
      localStorage.removeItem(buildSharedExplicitKey(storeId));
    }
    debugActiveCart('shared-explicit:set', { storeId, isEnabled });
  } catch (error) {
    debugActiveCart('shared-explicit:set:error', { storeId, isEnabled, error });
  }
};

export const getActiveSharedCartExplicitByStore = (storeId: string): boolean => {
  if (!storeId || typeof window === 'undefined') return false;
  try {
    const isEnabled = localStorage.getItem(buildSharedExplicitKey(storeId)) === '1';
    debugActiveCart('shared-explicit:get', { storeId, isEnabled });
    return isEnabled;
  } catch (error) {
    debugActiveCart('shared-explicit:get:error', { storeId, error });
    return false;
  }
};

// Shared cart token persistence
const ACTIVE_SHARED_CART_TOKEN_KEY = 'active_shared_cart_token';

export const setActiveSharedCartToken = (token: string | null): void => {
  // Tokenless collaborative flow: keep API for compatibility but don't persist tokens.
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(ACTIVE_SHARED_CART_TOKEN_KEY);
    if (token) {
      debugActiveCart('shared-token:ignored', { reason: 'tokenless-flow', tokenLength: token.length });
    } else {
      debugActiveCart('shared-token:cleared');
    }
  } catch (error) {
    debugActiveCart('shared-token:set:error', { error });
  }
};

export const getActiveSharedCartToken = (): string | null => {
  // Tokenless collaborative flow.
  return null;
};

export type ActiveCollaborativeMeta = {
  isActive: boolean;
  sharedToken: string | null;
  activeCartId: string | null;
};

export const getActiveCollaborativeMetaForStore = (
  storeId: string,
  options?: { search?: string }
): ActiveCollaborativeMeta => {
  if (!storeId) {
    return { isActive: false, sharedToken: null, activeCartId: null };
  }

  const activePreferenceId = getActiveCartPreference(storeId);
  const activeSnapshot = getActiveCartSnapshot(storeId);
  const sharedExplicit = getActiveSharedCartExplicitByStore(storeId);

  const search = options?.search
    ?? (typeof window !== 'undefined' ? window.location.search : '');
  void search;

  const activeCartId = activePreferenceId || activeSnapshot?.cartId || null;
  // A cart is a shared-with-me cart when its ID starts with 'shared-cart-'.
  const activeIsIncomingShared = !!activeCartId && activeCartId.startsWith('shared-cart-');
  const snapshotMatchesActive = activeSnapshot?.storeId === storeId
    && !!activeSnapshot?.cartId
    && (!activeCartId || activeSnapshot.cartId === activeCartId);

  // Tokenless collaborative mode:
  // consider collaborative if current snapshot marks shared, or user explicitly selected
  // collaborative mode, or active cart id corresponds to a shared-with-me cart id.
  const snapshotShared = !!(snapshotMatchesActive && activeSnapshot?.isShared);
  const isActiveCollaborative = snapshotShared || sharedExplicit || activeIsIncomingShared;

  return {
    isActive: isActiveCollaborative,
    sharedToken: null,
    activeCartId,
  };
};

export type CollaborativeActivitySignal = {
  storeId: string;
  cartId: string;
  units: number;
  total: number;
  storeName?: string;
  storeSlug?: string;
  collaboratorColor?: string | null;
};

const applyCollaborativeActivitySignal = (signal: CollaborativeActivitySignal): void => {
  if (!signal?.storeId || !signal?.cartId) return;

  setActiveCartPreference(signal.storeId, signal.cartId);
  setActiveSharedCartExplicitByStore(signal.storeId, true);
  setActiveCartSnapshot({
    storeId: signal.storeId,
    cartId: signal.cartId,
    isShared: true,
    sharedToken: null,
    units: Number.isFinite(signal.units) ? Math.max(0, Math.trunc(signal.units)) : 0,
    total: Number.isFinite(signal.total) ? Math.max(0, signal.total) : 0,
    storeName: signal.storeName,
    storeSlug: signal.storeSlug,
    updatedAt: new Date().toISOString(),
  });
  setActiveSharedCartRole('owner');
  if (typeof signal.collaboratorColor !== 'undefined') {
    setActiveSharedCartCollaboratorColor(signal.collaboratorColor || null);
  }
  setActiveSharedCartThemePriority(true);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('cart-active-changed', {
      detail: {
        storeId: signal.storeId,
        cartId: signal.cartId,
        isShared: true,
      },
    }));
    window.dispatchEvent(new CustomEvent('cart-updated'));
  }
};

let collaborativeSignalManagerInitialized = false;

export const initializeActiveCartTruthManagerSignals = (): void => {
  if (typeof window === 'undefined' || collaborativeSignalManagerInitialized) return;

  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<CollaborativeActivitySignal>;
    const signal = customEvent?.detail;
    if (!signal?.storeId || !signal?.cartId) return;
    applyCollaborativeActivitySignal(signal);
  };

  window.addEventListener(ACTIVE_COLLABORATIVE_ACTIVITY_SIGNAL_EVENT, handler as EventListener);
  collaborativeSignalManagerInitialized = true;
  debugActiveCart('truth-manager:signals-initialized');
};

export const emitCollaborativeActivitySignal = (signal: CollaborativeActivitySignal): void => {
  if (!signal?.storeId || !signal?.cartId) return;

  if (typeof window === 'undefined') {
    applyCollaborativeActivitySignal(signal);
    return;
  }

  window.dispatchEvent(new CustomEvent(ACTIVE_COLLABORATIVE_ACTIVITY_SIGNAL_EVENT, {
    detail: signal,
  }));
};
